'use client'
import {
    GetHubConnectionSettingsAction,
    type HubConnectionSettingsType,
} from '@/actions/get-hub-connection-settings-action'
import type { CustomSession } from '@/types/auth'
import { parseBackendErrorMessage } from '@/utils/errors'
import * as signalR from '@microsoft/signalr'
import { useAtom, useSetAtom } from 'jotai'
import { useSession } from 'next-auth/react'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type JSX, type ReactNode } from 'react'
import { useToaster } from '../toast/toasts-context'
import { hubConnectionAtom, hubConnectionStateAtom } from './atoms-provider'

type SignalRContextProps = {
    hubConnection: signalR.HubConnection | undefined
    connect: () => Promise<void>
    disconnect: () => Promise<void>
    subscribe: (event: string, callback: (...args: unknown[]) => void) => void
    unsubscribe: (event: string, callback: (...args: unknown[]) => void) => void
}

const SignalRContext = createContext<SignalRContextProps | undefined>(undefined)

type SignalRProviderProps = {
    children: ReactNode
}

function SignalRProvider({ children }: SignalRProviderProps): JSX.Element {
    const [hubConnection, setHubConnection] = useAtom(hubConnectionAtom)
    const setHubConnectionState = useSetAtom(hubConnectionStateAtom)

    const toaster = useToaster()
    const session = useSession()
    const sessionData = session.data as CustomSession | undefined
    const [settings, setSettings] = useState<HubConnectionSettingsType>()
    const connectionTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

    const fetchSettings = useCallback(async (): Promise<void> => {
        GetHubConnectionSettingsAction()
            .then(setSettings)
            .catch((error: unknown) => {
                const errorMessage = parseBackendErrorMessage(error as string)

                const errorMessages: string[] = []
                errorMessages.push('Impossibile ottenere le impostazioni di connessione al server delle notifiche')
                if (process.env.NODE_ENV === 'development' && errorMessage) {
                    errorMessages.push(errorMessage)
                }
                toaster.add({
                    type: 'error',
                    content: errorMessages.join(' | '),
                    timeout: 5000,
                    variant: 'solid',
                })
            })
    }, [toaster])

    const buildConnectionHub = useCallback(async (): Promise<void> => {
        try {
            if (!settings || !sessionData || !sessionData.accessToken) {
                return
            }
            const newConnection = new signalR.HubConnectionBuilder()
                .withUrl(settings.url, {
                    withCredentials: true,
                    accessTokenFactory: () => {
                        if (!sessionData.accessToken)
                            throw new Error('Impossibile collegarsi al server delle notifiche senza sessione.')
                        return sessionData.accessToken
                    },
                })
                .withAutomaticReconnect(Array.from({ length: 100 }).map(() => 5000))
                .build()
            newConnection.keepAliveIntervalInMilliseconds = 1000 * 15
            newConnection.serverTimeoutInMilliseconds = 1000 * 60

            newConnection.onreconnecting(() => {
                setHubConnectionState(signalR.HubConnectionState.Reconnecting)
            })

            newConnection.onreconnected(() => {
                setHubConnectionState(signalR.HubConnectionState.Connected)
            })

            setHubConnection(newConnection)
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error)

            setHubConnectionState(signalR.HubConnectionState.Disconnected)
            toaster.add({
                content: (
                    <>
                        <div>Connessione al server delle notifiche fallita.</div>
                        <small>{errorMessage}</small>
                    </>
                ),
                variant: 'solid',
                type: 'error',
                timeout: 5000,
            })
        }
    }, [sessionData, setHubConnection, setHubConnectionState, settings, toaster])

    const connect = useCallback(async (): Promise<void> => {
        if (!hubConnection) {
            return
        }

        clearTimeout(connectionTimeout.current)

        connectionTimeout.current = setTimeout(() => {
            hubConnection
                .start()
                .then(async () => {
                    if (settings) {
                        switch (settings.role) {
                            case 'Admin': {
                                await hubConnection.invoke('JoinGroup', 'Admins')
                                break
                            }
                            default: {
                                await hubConnection.invoke('JoinGroup', settings.company)
                                break
                            }
                        }
                    }
                    setHubConnectionState(signalR.HubConnectionState.Connected)
                    // toaster.add({
                    //     content: 'Connesso al server delle notifiche.',
                    //     variant: 'simple',
                    //     type: 'success',
                    //     timeout: 5000,
                    // })
                })
                .catch((error: unknown) => {
                    const errorMessage = error instanceof Error ? error.message : String(error)

                    setHubConnectionState(signalR.HubConnectionState.Disconnected)

                    if (errorMessage.includes('401')) {
                        buildConnectionHub()
                        return
                    }

                    toaster.add({
                        content: (
                            <>
                                <div>Connessione al server delle notifiche fallita.</div>
                                <small>{errorMessage}</small>
                            </>
                        ),
                        variant: 'solid',
                        type: 'error',
                        timeout: 5000,
                    })
                })
        }, 100)
    }, [hubConnection, settings, setHubConnectionState, toaster, buildConnectionHub])

    const disconnect = useCallback(async (): Promise<void> => {
        hubConnection?.stop().then(() => {
            setHubConnectionState(signalR.HubConnectionState.Disconnected)
            // toaster.add({
            //     content: 'Disconnesso dal server delle notifiche.',
            //     variant: 'simple',
            //     type: 'info',
            //     timeout: 5000,
            // })
        })
    }, [hubConnection, setHubConnectionState])

    useEffect(() => {
        const work = async (): Promise<void> => {
            if (!settings) {
                await fetchSettings()
            }
            if (!hubConnection) {
                await buildConnectionHub()
            }

            if (!hubConnection) {
                return
            }

            if (hubConnection.state === signalR.HubConnectionState.Disconnected && session.status === 'authenticated') {
                connect()
            }

            if (hubConnection.state === signalR.HubConnectionState.Connected && session.status === 'unauthenticated') {
                disconnect()
            }
        }

        work()
    }, [buildConnectionHub, connect, disconnect, fetchSettings, hubConnection, session.status, settings])

    const subscribe = (event: string, callback: (...args: unknown[]) => void): void => {
        if (hubConnection) {
            hubConnection.on(event, (...args) => {
                callback(args)
            })
        }
    }

    const unsubscribe = (event: string, callback: (...args: unknown[]) => void): void => {
        if (hubConnection) {
            hubConnection.off(event, (...args) => {
                callback(args)
            })
        }
    }

    return (
        <SignalRContext.Provider value={{ hubConnection, connect, disconnect, subscribe, unsubscribe }}>
            {children}
        </SignalRContext.Provider>
    )
}

const useSignalR = (): SignalRContextProps => {
    const context = useContext(SignalRContext)
    if (!context) {
        throw new Error("useSignalR deve essere usato all'interno di un SignalRProvider")
    }
    return context
}

export { SignalRProvider, useSignalR }
